import pickExcept from 'utils/pickExcept';
import pick from 'utils/pick';
import { getCustomFonts } from 'utils/transform/template';
import uniq from 'utils/uniq';
import uniqBy from 'utils/uniqBy';
import uuid from 'utils/uuid';
import { isUrlValid } from 'utils/validator';
import queryString from 'utils/queryString';
import {
  getAssetUrl,
  getCurrentPhoto,
  getOriginalPage,
  isBackgroundPlaceholder,
  isFrameInSafeZone,
  isPhotoPlaceholder,
  isStaticPlaceholder,
  isTextPlaceholder,
  isVectorPlaceholder,
  normalizeTextPlaceholderValue,
  PLACEHOLDER_TYPE,
} from './template.js';

const DONT_PICK_THESE_PAGE_KEYS = [
  'id',
  'key',
  'index',
  'type',
  'spine',
  'span',
  'pageNumber',
  'pageLabel',
  'pageType',
  'placeholders',
  'override',
];

const formatOverride = override => {
  if (override == null) {
    return undefined;
  }
  if (typeof override !== 'object') {
    return undefined;
  }
  if (Object.keys(override).length === 0) {
    return undefined;
  }
  return override;
};

/**
 * Parse server (order, draft or cart) data to client data.
 * @param {object} template
 * @param {object} data
 * @param {object} config
 * @param {boolean} config.onlyFirstPage
 * @param {boolean} config.useServerAssets
 * @param {string[]} config.withPageParams
 * @returns {object}
 */
export function parseData(template, data = {}, config = {}) {
  const photos = data.photos || [];
  const pages = [];
  const customFonts = [];
  const additionalInfo = data.additional_info || {};
  const favorite = data.favorite || {};

  let originalPages = [];
  if (data.cover) {
    originalPages.push({ ...data.cover, type: 'cover' });
  }
  if (data.pages) {
    originalPages.push(...data.pages.map(page => ({ ...page, type: 'pages' })));
  }

  if (config.onlyFirstPage) {
    originalPages = originalPages.slice(0, 1);
  }

  let duplicatePlaceholders = 0;

  originalPages.forEach(page => {
    const pageOverride = { ...(page.override ?? {}) };
    const placeholders = [];

    // specs defines dynamic placeholders
    const specs = page.specs ?? [];

    (page.placeholders || []).forEach(placeholder => {
      const placeholderSpec = specs.find(spec => spec.id === placeholder.id) ?? {};
      const override = { ...placeholderSpec, ...placeholder.override };
      const polotno = placeholder.polotno ?? undefined;

      if (isBackgroundPlaceholder(placeholder)) {
        const url = placeholder.value;
        if (isUrlValid(url)) {
          pageOverride.backgroundImage = url;
        }
        return;
      }

      if (isPhotoPlaceholder(placeholder)) {
        let url = placeholder.value;
        let mediumId = placeholder.mediumId || undefined;

        const defaultValue =
          placeholder.defaultValue || placeholder.photo_defaultValue || undefined;
        const urlFromServer = placeholder.value_url;
        if (defaultValue) {
          url = getAssetUrl(
            template,
            `/${config.useServerAssets ? 'assets_server' : 'assets_client'}/images/${defaultValue}`,
          );
        } else if (urlFromServer) {
          if (urlFromServer.indexOf('X-Amz-Signature=') >= 0) {
            url = queryString.parseUrl(urlFromServer).url;
          } else {
            url = urlFromServer;
          }
          if (typeof placeholder.value === 'number') {
            mediumId = placeholder.value;
          }
        }

        let photoData = {};
        if (url) {
          let index = photos.findIndex(photo => photo.url === url);
          if (index < 0) {
            const getTags = () => {
              const tags = [];
              // original tags
              if (Array.isArray(placeholder.photo_tags)) {
                tags.push(...placeholder.photo_tags);
              }
              // in some cases, tags are not saved
              if (placeholder.id.indexOf('layer_') === 0) {
                tags.push('layer');
              }
              return uniq(tags);
            };

            index =
              photos.push({
                key: uuid(),
                url,
                defaultValue,
                alive: 1,
                mediumId,
                width: placeholder.photo_width || undefined,
                height: placeholder.photo_height || undefined,
                datetime: placeholder.photo_datetime || undefined,
                tags: getTags(),
                type: placeholder.photo_type || undefined,
              }) - 1;
          }

          photoData = {
            photo_key: photos[index].key,
            x: placeholder.center[0],
            y: placeholder.center[1],
            scale: placeholder.scale,
            orientation: placeholder.orientation,
          };
        }

        placeholders.push({
          id: placeholder.id,
          type: PLACEHOLDER_TYPE.PHOTO,
          override,
          polotno,
          data: photoData,
        });
        return;
      }

      if (isTextPlaceholder(placeholder)) {
        let textData = {};
        if (placeholder.value) {
          textData = {
            value: normalizeTextPlaceholderValue(placeholder.value),
            value_original: placeholder.value_original,
            value_enhanced: placeholder.value_enhanced,
          };
        }

        if (override.fontName) {
          customFonts.push(override.fontName);
        }

        placeholders.push({
          id: placeholder.id,
          type: PLACEHOLDER_TYPE.TEXT,
          override,
          polotno,
          data: textData,
        });
        return;
      }

      if (isVectorPlaceholder(placeholder)) {
        placeholders.push({ id: placeholder.id, type: PLACEHOLDER_TYPE.VECTOR, override, polotno });
        return;
      }

      if (isStaticPlaceholder(placeholder)) {
        placeholders.push({ id: placeholder.id, type: PLACEHOLDER_TYPE.STATIC, override, polotno });
        return;
      }

      console.error(`Unsupported placeholder ${placeholder.id} type "${placeholder.type}"!`);
    });

    const uniqPlaceholders = uniqBy(placeholders, 'id');

    pages.push({
      key: uuid(),
      id: page.id,
      ...pickExcept(page, ...DONT_PICK_THESE_PAGE_KEYS),
      ...pick(page, ...(config.withPageParams ?? [])),
      type: page.type,
      placeholders: uniqPlaceholders,
      override: pageOverride,
    });

    if (uniqPlaceholders.length !== placeholders.length) {
      duplicatePlaceholders += placeholders.length - uniqPlaceholders.length;
    }
  });

  if (duplicatePlaceholders > 0) {
    console.log({ duplicatePlaceholders });
  }

  if (data.photos) {
    console.log(`Photos from pages: ${photos.length}. Photos from data: ${data.photos.length}`);
  }

  return {
    photos,
    pages,
    favorite,
    additionalInfo,
    fonts: getCustomFonts(template, 'all', customFonts),
  };
}

/**
 * Format client data to server data (cart or draft)
 * @param {object} template
 * @param {object} data
 * @param {Array} data.pages
 * @param {Array} data.photos
 * @param {object} data.additionalInfo
 * @param {object} data.favorite
 * @param {object} config
 * @param {object} config.validator
 * @param {string[]} config.withPageParams
 * @returns {object}
 */
export function formatData(template, { pages, photos, additionalInfo, favorite }, config = {}) {
  const data = {
    additional_info: additionalInfo ?? undefined,
    favorite: favorite ?? {},
    pages: [],
    photos: photos ?? undefined,
  };

  const isCoverPage = (page, index) => {
    if (page.type === 'cover') {
      return true;
    }
    if (page.type === 'pages') {
      return false;
    }
    return template.cover && index === 0;
  };

  pages.forEach((page, index) => {
    if (page == null) {
      return;
    }
    const pageOverride = { ...(page.override ?? {}) };
    // When validator is not null, it means prepare data for the server.
    // So it is useless to format placeholders which are not the part of the original page.
    // Otherwise, without validator, save all of data as a draft.
    const originalPage = config.validator
      ? getOriginalPage(template, page, isCoverPage(page, index) ? 'cover' : 'pages')
      : page;

    let pageLabel = '.';
    if (pages.length > 1) {
      pageLabel = isCoverPage(page, index)
        ? ' on Cover Page.'
        : ` on Page ${template.cover ? index : index + 1}.`;
    }

    const placeholders = [];

    (originalPage.placeholders || []).forEach(placeholder => {
      const override = formatOverride(placeholder.override);
      const polotno = placeholder.polotno ?? undefined;

      if (isBackgroundPlaceholder(placeholder)) {
        const url = placeholder.data.photo_url;
        if (isUrlValid(url)) {
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.PHOTO,
            center: [0.5, 0.5],
            value: url,
            orientation: 0,
            scale: 1,
            override,
            polotno,
          });

          pageOverride.backgroundImage = undefined;
        }
        return;
      }

      if (isPhotoPlaceholder(placeholder)) {
        let isPhotoValid = false;

        const photoData = placeholder.data || {};

        const { photo, defaultValue, error } = getCurrentPhoto(photos, {
          key: photoData.photo_key,
        });

        if (defaultValue) {
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.PHOTO,
            center: [0.5, 0.5],
            defaultValue,
            orientation: 0,
            scale: 1,
            override,
            polotno,

            photo_width: photo.width || undefined,
            photo_height: photo.height || undefined,
            photo_datetime: photo.datetime || undefined,
            photo_tags: photo.tags || [],
            photo_type: photo.type || undefined,
          });

          isPhotoValid = true;
        } else if (photo) {
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.PHOTO,
            center: [photoData.x ?? 0.5, photoData.y ?? 0.5],
            value: photo.url,
            mediumId: photo.mediumId || undefined,
            orientation: photoData.orientation ?? 0,
            scale: photoData.scale ?? 1,
            override,
            polotno,

            photo_width: photo.width || undefined,
            photo_height: photo.height || undefined,
            photo_datetime: photo.datetime || undefined,
            photo_tags: photo.tags || [],
            photo_type: photo.type || undefined,
          });

          isPhotoValid = true;
        } else {
          if (!config.validator) {
            // Add also empty placeholder when validator is undefined (draft)
            placeholders.push({
              id: placeholder.id,
              type: PLACEHOLDER_TYPE.PHOTO,
              override,
              polotno,
            });
          }

          isPhotoValid = false;
        }

        if (config.validator) {
          if (!isPhotoValid) {
            if (placeholder.required || config.validator.config.requiredPhotos) {
              config.validator.critical(`Please add or replace missing photo(s)${pageLabel}`);
            } else if (error) {
              config.validator.critical(`Please remove or replace ${error}${pageLabel}`);
            }
          } else {
            const photoWidth = photo.width;
            const photoHeight = photo.height;
            const scale = photoData.scale;
            const orientation = photoData.orientation

            const frameWidth = override?.frame[2] ?? placeholder.frame[2]
            const frameHeight = override?.frame[3] ?? placeholder.frame[3]

            if (frameWidth && frameHeight) {
              const currentDPI = Math.min(
                photoWidth / (((orientation % 2 === 0 ? frameWidth : frameHeight) / 2.54) * scale),
                photoHeight / (((orientation % 2 === 0 ? frameHeight : frameWidth) / 2.54) * scale),
              );

              if (currentDPI < 70) {
                config.validator.warning(`Please add higher quality version of photo${pageLabel}`);
              }
            }
          }
        }
        return;
      }

      if (isTextPlaceholder(placeholder)) {
        let isTextValid = false;

        const textData = placeholder.data || {};

        const value = textData.value ?? '';

        if (value.length > 0) {
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.TEXT,
            value: normalizeTextPlaceholderValue(value),
            value_original: textData.value_original,
            value_enhanced: textData.value_enhanced,
            override,
            polotno,
          });

          isTextValid = true;
        } else if (!config.validator) {
          // Add also empty placeholder when validator is undefined (draft)
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.TEXT,
            override,
            polotno,
          });

          isTextValid = false;
        }

        if (config.validator) {
          if (!isTextValid && config.validator.config.requiredTexts) {
            config.validator.critical(`Please add or replace missing text(s)${pageLabel}`);
          }

          if (originalPage.bleed && !isFrameInSafeZone(originalPage.safeFrame, override?.frame, override?.angle, originalPage.size.offsetX)) {
            config.validator.warning(`Items placed in the page edges${pageLabel}`);
          }
        }
        return;
      }

      if (isVectorPlaceholder(placeholder)) {
        placeholders.push({
          id: placeholder.id,
          type: PLACEHOLDER_TYPE.VECTOR,
          override,
          polotno,
        });
        // TODO validation?
        return;
      }

      if (isStaticPlaceholder(placeholder)) {
        placeholders.push({
          id: placeholder.id,
          type: PLACEHOLDER_TYPE.STATIC,
          override,
          polotno,
        });
        return;
      }

      console.error(`Unsupported placeholder ${placeholder.id} type "${placeholder.type}"!`);
    });

    const pageItem = {
      id: originalPage.id,
      ...pickExcept(page, ...DONT_PICK_THESE_PAGE_KEYS),
      ...pick(page, ...(config.withPageParams ?? [])),
      placeholders,
      override: formatOverride(pageOverride),
    };

    if (isCoverPage(page, index)) {
      data.cover = pageItem;
    } else {
      data.pages.push(pageItem);
    }
  });

  return data;
}
